/**
 * Configuration for the 3-element high-lift airfoil, organized as a 3-level
 * hierarchy that mirrors the design dependency chain:
 *
 *   L1  main      — the base airfoil (fixed LS(1)-0417). Changing it invalidates
 *                   everything downstream.
 *   L2  design    — the high-lift element design built on that airfoil: main cove
 *                   cutout, vane & flaperon shapes, flap track, flaperon hinge.
 *   L3  operation — the operating point on a fixed design: deployment + flaperon angle.
 *
 * Single source of truth for the default geometry consumed by the /highlift
 * design tool and by the geometry engine in ./geometry.ts. Originally ported from
 * ~/himalaya/geometry/airfoils/estol_config.yaml.
 */

// Element + blowing collection types live in ./elements (the FXC-9009 §B.5 restructure). They are
// re-exported through the design fields below so estolConfig stays the single type entry point.
// `elements.ts` only imports TYPES from this module (erased at runtime), so the value import here
// is not a runtime cycle.
import type { Element, BlowingSource } from './elements';
import {
  HIGH_LIFT_SCHEMA_VERSION,
  DEFAULT_MAIN_ELEMENT_ID,
  DEFAULT_VANE_ELEMENT_ID,
  DEFAULT_FLAPERON_ELEMENT_ID,
  DEFAULT_BLOWING_SOURCE_ID,
} from './elements';

export type V2 = [number, number];

/** Explicit upper/lower surface coordinates for a tabulated airfoil (e.g. LS(1)-0417). */
export interface AirfoilCoords {
  /** LE → TE, ascending x. */
  upper: V2[];
  /** LE → TE, ascending x. */
  lower: V2[];
}

/**
 * A HINGE-CONCENTRIC cove back wall (FXC-9009 control-surface tranche — the internally-hinged sealed
 * aileron; ported from the CS designer's `compute_cs_shape` spiral cove). When present on a
 * {@link CoveCutouts}, the main cove's back is a SPIRAL ARC centred on the flap `hinge` (global
 * coords), NOT the default vertical wall at `coveX`.
 *
 * The arc's radius is a cosine blend between the LOWER split radius `rLo` and the UPPER split radius
 * `rUp` as the angle sweeps from `aLo` to `aUp` (the long way, through the −x front). The split radii
 * are the perpendicular (nearest-surface) distances from the hinge to the lower/upper skins, so the
 * flap NOSE spiral uses (`rLo`, `rUp`) and the main COVE spiral uses (`rLo + gap`, `rUp + gap`). Both
 * spirals share the SAME angle→radius profile and rotate about the SAME hinge, so a circle (spiral)
 * rotated about its own centre maps to itself and the slot clearance stays exactly `gap` at EVERY
 * deflection. Absent ⇒ the legacy vertical back wall (the default cove is byte-identical).
 * See {@link buildMainWing} / buildConcentricFlap.
 */
export interface ConcentricCoveArc {
  /** Rotation hinge (global coords, stowed state) — the shared centre of the flap-nose + cove spirals. */
  hinge: V2;
  /** Lower split radius (chords): hinge → nearest point on the LOWER skin. */
  rLo: number;
  /** Upper split radius (chords): hinge → nearest point on the UPPER skin. */
  rUp: number;
  /** Angle (rad) of the lower split point about the hinge (arc start). */
  aLo: number;
  /** Angle (rad) of the upper split point about the hinge (arc end, reached the long way through −x). */
  aUp: number;
  /** Slot gap (chords): the COVE spiral radii are the flap-nose spiral radii + this. */
  gap: number;
}

/**
 * Cove-cutout parameters for the main element (chord-normalized). The cove
 * follows the airfoil's own upper and lower surfaces (offset inward by the
 * blunt-TE thickness) from each cut forward to `coveX`, joined by a vertical back
 * wall at `coveX` with sharp corners. `coveX` sits forward of both cuts.
 */
export interface CoveCutouts {
  /** x where the upper surface is cut (the lip). */
  upperCutX: number;
  /** x where the lower surface is cut. */
  lowerCutX: number;
  /** x of the cove back wall (forward of both cuts). */
  coveX: number;
  /**
   * OPTIONAL hinge-concentric back wall (FXC-9009 internally-hinged aileron). Absent ⇒ the legacy
   * VERTICAL back wall at `coveX` (default cove byte-identical). Present ⇒ {@link buildMainWing}
   * replaces the vertical wall with a SPIRAL ARC about `arc.hinge`, so the flap's concentric nose
   * clears it by a CONSTANT gap through the full rotation. See {@link ConcentricCoveArc}.
   */
  arc?: ConcentricCoveArc;
}

/** A NACA 4-digit element anchored between an LE and TE point. */
export interface NacaElementCfg {
  /** 4-digit NACA designation, e.g. "9621". */
  naca: string;
  /** Stowed leading-edge anchor (global coordinates). */
  stowedLe: V2;
  /** Stowed trailing-edge anchor (global coordinates). */
  stowedTe: V2;
}

/**
 * Flaperon shape, derived from the main airfoil rather than an independent foil.
 * Aft of `upperCutX` / `lowerCutX` the surfaces are exactly LS(1)-0417 (blunt-
 * truncated at the TE like the other elements). Forward of the cuts the nose is a
 * 5-control-point clamped cubic B-spline whose end tangents match the airfoil
 * slope at each cut: P0 = upper cut, P1 = P0 + noseTangentUpper·tHat(upperCutX),
 * P2 = noseTip, P3 = P4 + noseTangentLower·tHat(lowerCutX), P4 = lower cut.
 * Defined in the airfoil (global) frame; deployment is applied on top.
 */
/**
 * Vane shape: a free-form outline (no longer a NACA foil). The outline is a closed
 * periodic cubic B-spline through 5 control points — the trailing-edge location
 * `te` plus 4 other `points` (loop order [te, ...points]) — with a blunt TE of the
 * airfoil's `bluntThickness` cut at `te` (shared with the cove lips and flaperon).
 */
export interface VaneShape {
  /** Trailing-edge location; the blunt TE is cut here. */
  te: V2;
  /** The 4 other B-spline control points (with `te`, a 5-point closed loop). */
  points: V2[];
}

export interface FlaperonShape {
  /** Upper-surface cut x; aft of it the upper surface = the airfoil. */
  upperCutX: number;
  /** Lower-surface cut x; aft of it the lower surface = the airfoil. */
  lowerCutX: number;
  /** P1 distance from the upper cut along the forward airfoil tangent. */
  noseTangentUpper: number;
  /** P3 distance from the lower cut along the forward airfoil tangent. */
  noseTangentLower: number;
  /** P2, the free middle nose control point (global coordinates). */
  noseTip: V2;
}

// ---------------------------------------------------------------------------
// Level 1 — main airfoil (foundational; changing it invalidates everything below)
// ---------------------------------------------------------------------------

export interface MainAirfoil {
  /** Base airfoil coordinates (fixed to LS(1)-0417 for now). */
  coords: AirfoilCoords;
  /** Flat blunt TE thickness, in overall-chord units, applied to all elements. */
  bluntThickness: number;
  /** Physical chord length (m). Sets the length scale for the Reynolds number; the geometry
   *  itself is normalized to chord = 1. */
  chord: number;
}

// ---------------------------------------------------------------------------
// Level 2 — high-lift element design (the system geometry on the main airfoil)
// ---------------------------------------------------------------------------

/**
 * A single hinge pivot (global coords, at the stowed state). Used both for the deploy
 * hinge (the whole vane+flaperon assembly rotates about it by the deploy angle) and the
 * flaperon hinge (the flaperon rotates about it relative to the assembly). The rotation
 * angles are operating-point quantities and live in {@link Operation}.
 */
export interface Hinge {
  /** Pivot location (global coords, stowed state). */
  pivot: V2;
}

/**
 * Parametric deployment TRACK PATH for the flap (vane+flaperon) assembly. A real
 * Fowler-type flap does not pivot about one fixed point — its hinge carriage rolls
 * AFT and DOWN along a curved track while the element rotates, opening a slot. This
 * generalizes the single-pivot model: the effective pivot translates along the track
 * by an amount set by the {@link MotionLaw}, and the assembly rotates about that
 * translated pivot.
 *
 * The track is a clamped cubic B-spline through `pivot` (= the stowed pivot, deploy
 * fraction 0) plus the ordered control `waypoints` (the carriage path as the flap
 * extends). With an EMPTY `waypoints` list the track is degenerate (zero length): the
 * pivot never moves and the mechanism reduces EXACTLY to single-pivot rotation about
 * `pivot` — the backward-compatible default. Coordinates are the global airfoil frame.
 */
export interface DeployTrack {
  /** Stowed pivot (track start, deploy fraction 0). Equals the legacy `deployHinge.pivot`. */
  pivot: V2;
  /** Ordered carriage waypoints aft of `pivot` (B-spline control points). Empty ⇒ pure rotation. */
  waypoints: V2[];
}

/**
 * The MOTION LAW relating the deploy fraction f∈[0,1] to the mechanism state. The fraction
 * is the normalized deploy command: f = deployAngleDeg / `fullDeployAngleDeg`. From f the law
 * derives:
 *   - translation:  arc-length position along the {@link DeployTrack} = `trackTravel`·g(f);
 *   - rotation:     element rotation angle = `fullDeployAngleDeg`·f  (deg, TE-down positive);
 *   - the gap/overlap then FALL OUT of the resulting deployed contours (measured in gapMetrics).
 *
 * `translationCurve` shapes g(f): the fraction of full track travel reached at deploy fraction f.
 *   - 'linear'    : g(f) = f                 (travel proportional to angle)
 *   - 'fowler'    : g(f) = f^`translationExponent` with exponent>1 ⇒ slow aft motion early then
 *                   rapid extension late (classic Fowler "translate then rotate")
 *   - 'rotateFirst': g(f) = f^`translationExponent` with exponent<1 ⇒ extend early, rotate late.
 * With an empty track these are all no-ops (g·0 = 0) and the law is pure rotation.
 */
export interface MotionLaw {
  /** Deploy angle at deploy fraction f=1 (deg). The legacy `operation.deployAngleDeg` maps to
   *  fraction = deployAngleDeg / fullDeployAngleDeg, so the angle stays the primary command. */
  fullDeployAngleDeg: number;
  /** Total arc length travelled along the track at full deploy (f=1), in chords. 0 ⇒ no travel. */
  trackTravel: number;
  /** Shape of the translation-vs-fraction schedule g(f). */
  translationCurve: 'linear' | 'fowler' | 'rotateFirst';
  /** Exponent for the 'fowler'/'rotateFirst' schedules (g(f)=f^exp). Ignored for 'linear'. */
  translationExponent: number;
}

/** Backward-compatible degenerate motion law: zero travel, pure rotation about the stowed pivot. */
export const PURE_ROTATION_MOTION: MotionLaw = {
  fullDeployAngleDeg: 40.0,
  trackTravel: 0.0,
  translationCurve: 'linear',
  translationExponent: 1.0,
};

/** Vane follower linkage parameters. See {@link HighLiftDesign.vaneFollower}. */
export interface VaneFollower {
  /** Rotation gain relative to the flap's deploy angle (0..1). 1 ⇒ locked to the flap (legacy);
   *  <1 ⇒ the vane deploys LESS than the flap, staying a shallower member of the slot. */
  gain: number;
  /** Arc-length lag along the shared deploy track (chords): the vane's carriage trails the flap's
   *  by this distance, so it holds a slot-gap behind the flap nose. No-op on a zero-travel track. */
  lagChords: number;
}

/** Identity follower — reduces to the legacy rigid assembly (gain 1, no lag). */
export const RIGID_VANE_FOLLOWER: VaneFollower = { gain: 1.0, lagChords: 0.0 };

/** A physically sensible double-slotted vane follower: the vane deploys to 60% of the flap angle
 *  and trails 0.03c behind along the track. Meaningful only with {@link FOLLOWER_DEPLOY_TRACK}. */
export const DEFAULT_VANE_FOLLOWER: VaneFollower = { gain: 0.6, lagChords: 0.03 };

/** Opt-in slotted/Fowler deploy track: the carriage rolls AFT (+x) and DOWN (−y) from the stowed
 *  deploy hinge, opening a real slot as the flap rotates (vs. pure single-pivot rotation). Pairs
 *  with {@link FOLLOWER_MOTION}. Installing this on a design changes its deployed geometry (and so
 *  its design_hash) — deliberately, it is a different mechanism — so it is NOT the frozen default. */
export const FOLLOWER_DEPLOY_TRACK: DeployTrack = {
  pivot: [0.71, -0.2],
  waypoints: [[0.755, -0.235], [0.80, -0.28]],
};

/** Motion law for the {@link FOLLOWER_DEPLOY_TRACK}: rotate-early / translate-late (Fowler), ~0.09c
 *  of aft+down travel at full deploy. */
export const FOLLOWER_MOTION: MotionLaw = {
  fullDeployAngleDeg: 40.0,
  trackTravel: 0.09,
  translationCurve: 'fowler',
  translationExponent: 2.0,
};

export interface HighLiftDesign {
  /** Main element cove cutout. */
  cove: CoveCutouts;
  /** Vane shape (free-form B-spline). */
  vane: VaneShape;
  /** Flaperon shape, derived from the main airfoil. */
  flaperon: FlaperonShape;
  /** Deploy hinge: the vane+flaperon assembly rotates about this pivot by the deploy angle.
   *  Legacy single-pivot description; `deployTrack.pivot` mirrors it when a track is present. */
  deployHinge: Hinge;
  /** Flaperon hinge pivot (the flaperon rotates about it within the assembly). */
  flaperonHinge: Hinge;
  /** Optional parametric deployment track. Absent (or empty waypoints) ⇒ single-pivot rotation
   *  about {@link deployHinge}; present ⇒ the pivot translates along the track per {@link motion}. */
  deployTrack?: DeployTrack;
  /** Optional motion law. Absent ⇒ {@link PURE_ROTATION_MOTION} (legacy behaviour). */
  motion?: MotionLaw;
  /**
   * Vane FOLLOWER linkage (FXC-9009 double-slotted tranche). Absent ⇒ the legacy RIGID assembly:
   * the vane rides the flap's deploy transform exactly (gain 1, zero lag), byte-identical. Present ⇒
   * the vane becomes a FOLLOWER on the same deploy {@link deployTrack}/{@link motion}: it rotates by
   * `gain`·deployAngle (gain < 1 ⇒ deploys LESS than the flap) and lags `lagChords` of track
   * arc-length BEHIND the flap carriage, so it trails a slot-gap behind the flap nose instead of
   * moving rigidly with it. `{ gain: 1, lagChords: 0 }` reduces EXACTLY to the rigid assembly (the
   * follower transform degenerates to the shared deploy transform), so identity is a no-op. The lag
   * term only bites on a NON-degenerate track (zero-travel track ⇒ arc-length offset is a no-op) —
   * see {@link FOLLOWER_DEPLOY_TRACK}/{@link FOLLOWER_MOTION} for the paired slotted track.
   */
  vaneFollower?: VaneFollower;
  /**
   * Canonical element collection (FXC-9009 §B.5). When present, the geometry builders + sweep
   * payload iterate this in order; absent ⇒ derived from the legacy `cove`/`vane`/`flaperon`
   * fields (see {@link ../elements.deriveElements}). The default config seeds it with
   * `[main-cove, vane-bspline, flaperon-derived]`. The element `cove`/`shape` objects reference the
   * legacy nested fields, which remain the values the UI + builders read this tranche.
   */
  elements?: Element[];
  /**
   * Canonical blowing-source collection (FXC-9009 §B.5). When present, the sweep payload iterates
   * this; absent ⇒ derived from the legacy top-level `propeller`. The default seeds it with the
   * single propeller as `blowing[0]`.
   */
  blowing?: BlowingSource[];
}

// ---------------------------------------------------------------------------
// Level 3 — operating point on a fixed design
// ---------------------------------------------------------------------------

/**
 * Upstream propeller, modeled as an actuator-disk momentum source (blown lift). This is
 * the disk GEOMETRY — where the propeller is and how big it is — a design-level sibling
 * of the high-lift element design. How hard it blows (the slipstream ratio Vj/V∞) is an
 * operating-point knob and lives in {@link Operation}. Coordinates are the global airfoil
 * frame (chords). The disk blows a slipstream downstream (+x) onto the wing.
 */
export interface Propeller {
  /** Disk center x (chords, global). Negative = ahead of the wing leading edge. */
  x: number;
  /** Disk center z (vertical, chords). */
  z: number;
  /** Disk diameter = slipstream-band height (chords). */
  diameter: number;
  /** Thrust-axis tilt from the chordwise (deg); positive deflects the jet upward. */
  tiltDeg: number;
}

export interface Operation {
  /** Deploy angle: the vane+flaperon assembly rotates about the deploy hinge (deg, positive
   *  = TE down / deployed; 0 = stowed). One-sided (down only). */
  deployAngleDeg: number;
  /** Flaperon rotation about its hinge, relative to the assembly (deg, positive = TE down;
   *  may reflex up via negative values, since it doubles as an aileron). */
  flaperonAngleDeg: number;
  /** Spoiler: upper cove-lip deflection about the coveX hinge (deg, positive = tip up). */
  spoilerAngleDeg: number;
  /** Propeller disk loading Δp/q∞ (q∞ = ½ρV∞²): 0 = off, >0 = thrust, <0 = suction/drag. */
  diskLoading: number;
  /** Freestream true airspeed (knots). With the chord, altitude and temperature it sets the
   *  Reynolds and Mach numbers (computed client-side; see flowConditions.ts). */
  freestreamKnots: number;
  /** Density altitude (m): the ISA altitude whose density equals the ambient density. */
  densityAltitudeM: number;
  /** Temperature offset from ISA at the density altitude (K); affects sound speed & viscosity. */
  tempOffsetK: number;
}

export interface HighLiftAirfoil {
  main: MainAirfoil;
  design: HighLiftDesign;
  /** Upstream propeller disk geometry (slipstream dialed by operation.vjOverVinf).
   *  Legacy single-disk field; mirrored into `design.blowing[0]` by the migration. */
  propeller: Propeller;
  operation: Operation;
  /**
   * Schema version (FXC-9009 §B.5). Absent on legacy-shaped configs; stamped to
   * {@link ../elements.HIGH_LIFT_SCHEMA_VERSION} once migrated so future migrations can
   * detect-and-upgrade. {@link DEFAULT_HIGH_LIFT_AIRFOIL} carries the current version.
   */
  schemaVersion?: number;
  /**
   * FXC-9009 design-program phase 2: per-knob fixed/free + limits, keyed by the flat-axis knob key
   * (see foil/knobRegistry.ts). Free knobs (the default) are the Sweep axes / Inverse variables;
   * fixed knobs are held. Optional + back-compatible — absent means every knob is free at its
   * default range.
   */
  constraints?: Record<string, { fixed: boolean; min?: number; max?: number }>;
}

/** NASA LS(1)-0417 ("GA(W)-1") tabulated coordinates. */
export const LS1_0417: AirfoilCoords = {
  upper: [
    [0.0, 0.0], [0.005, 0.0245], [0.0125, 0.0366], [0.025, 0.05], [0.05, 0.0678],
    [0.1, 0.0909], [0.15, 0.1065], [0.2, 0.1165], [0.25, 0.1225], [0.3, 0.1255],
    [0.35, 0.126], [0.4, 0.1245], [0.45, 0.1215], [0.5, 0.1165], [0.55, 0.1095],
    [0.6, 0.101], [0.65, 0.091], [0.7, 0.0795], [0.75, 0.0665], [0.8, 0.052],
    [0.85, 0.0365], [0.9, 0.0205], [0.95, 0.0085], [0.98, 0.0025], [1.0, 0.0],
  ],
  lower: [
    [0.0, 0.0], [0.005, -0.0152], [0.0125, -0.025], [0.025, -0.0345], [0.05, -0.0465],
    [0.1, -0.0585], [0.15, -0.0645], [0.2, -0.0675], [0.25, -0.0685], [0.3, -0.0685],
    [0.35, -0.0675], [0.4, -0.0655], [0.45, -0.0625], [0.5, -0.058], [0.55, -0.052],
    [0.6, -0.044], [0.65, -0.034], [0.7, -0.021], [0.75, -0.011], [0.8, -0.004],
    [0.85, 0.0], [0.9, 0.002], [0.95, 0.0015], [0.98, 0.0005], [1.0, 0.0],
  ],
};

/**
 * Generate tabulated {@link AirfoilCoords} for a NACA 4-digit section from the standard
 * thickness + camber formulae (chord normalised 0..1, TE closed). `code` is the 4-digit string
 * (e.g. "4412" = 4% camber at 0.4c, 12% thickness). Used to seed the {@link FOIL_LIBRARY} with
 * alternate base foils for the "Replace foil" affordance — same shape as {@link LS1_0417}
 * ({@link AirfoilCoords}: upper/lower as ascending-x LE→TE point lists).
 */
export function naca4digit(code: string, n = 24): AirfoilCoords {
  const m = parseInt(code[0] ?? '0', 10) / 100; // max camber
  const p = parseInt(code[1] ?? '0', 10) / 10; // camber position
  const t = parseInt(code.slice(2), 10) / 100; // max thickness
  // Cosine-clustered x (denser at the LE) over [0, 1].
  const xs: number[] = [];
  for (let i = 0; i <= n; i++) {
    const beta = (Math.PI * i) / n;
    xs.push(0.5 * (1 - Math.cos(beta)));
  }
  // Closed-TE thickness distribution (the −0.1036 coefficient closes the TE at x=1).
  const yt = (x: number): number =>
    5 * t * (0.2969 * Math.sqrt(x) - 0.126 * x - 0.3516 * x * x + 0.2843 * x ** 3 - 0.1036 * x ** 4);
  const camber = (x: number): { yc: number; dyc: number } => {
    if (p <= 0 || m <= 0) return { yc: 0, dyc: 0 };
    if (x < p) return { yc: (m / (p * p)) * (2 * p * x - x * x), dyc: (2 * m / (p * p)) * (p - x) };
    return {
      yc: (m / ((1 - p) ** 2)) * (1 - 2 * p + 2 * p * x - x * x),
      dyc: (2 * m / ((1 - p) ** 2)) * (p - x),
    };
  };
  const upper: V2[] = [];
  const lower: V2[] = [];
  for (const x of xs) {
    const th = yt(x);
    const { yc, dyc } = camber(x);
    const theta = Math.atan(dyc);
    upper.push([x - th * Math.sin(theta), yc + th * Math.cos(theta)]);
    lower.push([x + th * Math.sin(theta), yc - th * Math.cos(theta)]);
  }
  // Force exact LE (0,0) + closed TE endpoints so the contract matches LS1_0417 (ascending x, TE-closed).
  upper[0] = [0, 0];
  lower[0] = [0, 0];
  upper[upper.length - 1] = [1, 0];
  lower[lower.length - 1] = [1, 0];
  return { upper, lower };
}

/** NACA 4412 — a cambered general-aviation section, alternate base foil for the foil library. */
export const NACA_4412: AirfoilCoords = naca4digit('4412');

/** NACA 0012 — a symmetric section, a markedly different alternate base foil for the foil library. */
export const NACA_0012: AirfoilCoords = naca4digit('0012');

/**
 * The foil library: the base airfoils the workbench can swap the main element to via
 * `setMainCoords` (the "Replace foil" affordance + the tutorial's replace-foil step). The default
 * LS(1)-0417 leads. Each entry is `{ name, coords }` with the {@link AirfoilCoords} contract
 * (ascending-x LE→TE upper/lower, normalised chord 0..1, TE-closed).
 */
export const FOIL_LIBRARY: ReadonlyArray<{ name: string; coords: AirfoilCoords }> = [
  { name: 'LS(1)-0417', coords: LS1_0417 },
  { name: 'NACA 4412', coords: NACA_4412 },
  { name: 'NACA 0012', coords: NACA_0012 },
];

/**
 * A deterministic 32-bit digest of a main element's base airfoil — the foil's IDENTITY. Folded into
 * {@link flattenDesign} as the `main_foil_digest` axis so that swapping the base foil (via
 * `setMainCoords` / the "Replace foil" affordance) changes the client design fingerprint and so flips
 * `isDirty`; without it two genuinely different base foils collided to the same fingerprint. Coords
 * are rounded to 1e-6 before folding so float round-trips don't perturb the identity. This mirrors the
 * INTENT of the server's `design_coords_signature` (rans_provenance.py), which already folds
 * `main.coords` into the authoritative `design_hash`; here a single numeric axis is enough for the
 * client's dirty-detection + display identity (the server hash stays authoritative).
 */
export function mainCoordsDigest(coords: AirfoilCoords | undefined): number {
  let h = 0x811c9dc5;
  const step = (byte: number): void => {
    h ^= byte & 0xff;
    h = Math.imul(h, 0x01000193);
  };
  const fold = (n: number): void => {
    const q = Math.round((Number.isFinite(n) ? n : 0) * 1e6) | 0;
    step(q);
    step(q >>> 8);
    step(q >>> 16);
    step(q >>> 24);
  };
  const surface = (pts: ReadonlyArray<V2> | undefined): void => {
    if (!Array.isArray(pts)) return;
    step(pts.length);
    step(pts.length >>> 8);
    for (const p of pts) {
      fold(p[0]);
      fold(p[1]);
    }
  };
  surface(coords?.upper);
  surface(coords?.lower);
  return h >>> 0;
}

/**
 * Default configuration matching the reference paper geometry.
 *
 * Built via {@link buildDefaultHighLiftAirfoil} so the `design.elements` / `design.blowing`
 * collection references the SAME legacy nested objects (`design.cove`/`vane`/`flaperon`,
 * `propeller`) rather than independent copies — keeping the dual representation self-consistent
 * this tranche. Construct a fresh default via that builder if you need an independent object;
 * `useFoilDesign` always deep-clones the seed.
 */
function buildDefaultHighLiftAirfoil(): HighLiftAirfoil {
  const cove: CoveCutouts = { upperCutX: 0.815, lowerCutX: 0.66, coveX: 0.55 };
  const vane: VaneShape = {
    te: [0.75, 0.05],
    points: [[0.655, 0.055], [0.555, 0.015], [0.55, -0.08], [0.66, 0.045]],
  };
  const flaperon: FlaperonShape = {
    upperCutX: 0.9,
    lowerCutX: 0.75,
    noseTangentUpper: 0.025,
    noseTangentLower: 0.11,
    noseTip: [0.715, 0.075],
  };
  const propeller: Propeller = { x: -0.1, z: -0.2, diameter: 0.75, tiltDeg: 10.0 };
  const design: HighLiftDesign = {
    cove,
    vane,
    flaperon,
    deployHinge: { pivot: [0.71, -0.2] },
    flaperonHinge: { pivot: [0.76, -0.05] },
    // Default track is degenerate (no waypoints ⇒ zero travel), so the mechanism is exactly the
    // legacy single-pivot rotation about deployHinge. A real Fowler track would add aft+down
    // waypoints and a non-zero motion.trackTravel; see gapMetrics for the resulting slot.
    deployTrack: { pivot: [0.71, -0.2], waypoints: [] },
    motion: { ...PURE_ROTATION_MOTION },
    // Canonical collection: the default 3-element section + the single propeller as blowing[0].
    // Element shape/cove objects reference the legacy fields above so both views stay in sync.
    elements: [
      { kind: 'main-cove', id: DEFAULT_MAIN_ELEMENT_ID, label: 'Main', cove },
      { kind: 'vane-bspline', id: DEFAULT_VANE_ELEMENT_ID, label: 'Vane', shape: vane },
      { kind: 'flaperon-derived', id: DEFAULT_FLAPERON_ELEMENT_ID, label: 'Flaperon', shape: flaperon },
    ],
    blowing: [
      {
        id: DEFAULT_BLOWING_SOURCE_ID,
        label: 'Propeller',
        x: propeller.x,
        z: propeller.z,
        diameter: propeller.diameter,
        tiltDeg: propeller.tiltDeg,
      },
    ],
  };
  return {
    main: {
      coords: LS1_0417,
      bluntThickness: 0.003,
      chord: 1.0,
    },
    design,
    propeller,
    operation: {
      deployAngleDeg: 0.0,
      flaperonAngleDeg: 0.0,
      spoilerAngleDeg: 0.0,
      diskLoading: 0.0,   // 0 = propeller off (no thrust)
      freestreamKnots: 132.0,   // ≈ Mach 0.20 at sea-level ISA (chord 1 m ⇒ Re ≈ 4.6e6)
      densityAltitudeM: 0.0,
      tempOffsetK: 0.0,
    },
    schemaVersion: HIGH_LIFT_SCHEMA_VERSION,
  };
}

export const DEFAULT_HIGH_LIFT_AIRFOIL: HighLiftAirfoil = buildDefaultHighLiftAirfoil();
